import React from 'react';
import { Link } from 'react-router-dom';
import useTopSellersData, { type TopSeller } from '@/hooks/useTopSellersData';
import { categoryItems } from '@/data/foods';
import { specialtiesSection } from '@/data/content';
import OptimizedImage from '@/components/ui/OptimizedImage';
import { motion, AnimatePresence } from 'framer-motion';
import Button from '@/components/ui/Button';
import AddItemWithDatePopup from '@/components/features/AddItemWithDatePopup';
import { formatPrice } from '@/Services/format';

const TopSellerCategoryGrid: React.FC = () => {
    const { data: topSellers, isLoading, error } = useTopSellersData();

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.1 },
        },
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] as const },
        },
    };

    return (
        <section className="py-20 md:py-32 overflow-hidden relative">
            {/* Decorative Background Elements */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none -z-10">
                <div className="absolute top-[-10%] right-[-5%] w-[40%] h-[40%] rounded-full bg-app-primary/5 blur-[100px]" />
                <div className="absolute bottom-[-10%] left-[-5%] w-[40%] h-[40%] rounded-full bg-app-primary/5 blur-[100px]" />
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Section Header */}
                <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] as const }}
                        className="max-w-2xl"
                    >
                        <div className="flex items-center gap-2 mb-4">
                            <span className="h-px w-8 bg-app-primary" />
                            <span className="text-app-primary font-bold text-sm tracking-widest uppercase">
                                {specialtiesSection.badge}
                            </span>
                        </div>
                        <h2 className="text-3xl md:text-5xl font-black text-gray-900 dark:text-white leading-tight mb-4">
                            {specialtiesSection.heading.split(' ').slice(0, -1).join(' ')}{' '}
                            <span className="text-app-primary relative inline-block">
                                {specialtiesSection.heading.split(' ').slice(-1).join(' ')}
                                <svg
                                    className="absolute -bottom-2 left-0 w-full"
                                    viewBox="0 0 100 10"
                                    preserveAspectRatio="none"
                                >
                                    <path
                                        d="M0 5 Q 25 0, 50 5 T 100 5"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        fill="none"
                                        className="text-app-primary/30"
                                    />
                                </svg>
                            </span>
                        </h2>
                        <p className="text-lg text-gray-600 dark:text-gray-400">{specialtiesSection.description}</p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] as const }}
                    >
                        <Button
                            to="/menu"
                            variant="ghost"
                            className="group flex items-center gap-2"
                            icon={
                                <span className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center group-hover:bg-app-primary group-hover:text-white transition-all">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            d="M9 5l7 7-7 7"
                                        ></path>
                                    </svg>
                                </span>
                            }
                        >
                            {specialtiesSection.cta}
                        </Button>
                    </motion.div>
                </div>

                <div className="flex flex-col gap-20">
                    {/* Top Sellers Highlight */}
                    <div className="w-full">
                        <AnimatePresence mode="wait">
                            {isLoading || !topSellers || topSellers.length === 0 ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {[1, 2, 3].map((i) => (
                                        <div
                                            key={i}
                                            className="h-64 bg-gray-100 dark:bg-gray-800 rounded-[2.5rem] animate-pulse"
                                        />
                                    ))}
                                </div>
                            ) : (
                                <motion.div
                                    variants={containerVariants}
                                    initial="hidden"
                                    whileInView="visible"
                                    viewport={{ once: true }}
                                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                                >
                                    {/* Featured Item (First one) - Larger on desktop */}
                                    <motion.div
                                        variants={itemVariants}
                                        className="md:col-span-2 lg:col-span-2 group bg-white dark:bg-gray-800 rounded-[3rem] p-6 border border-gray-100 dark:border-gray-700 hover:border-app-primary/30 transition-all duration-500 shadow-sm hover:shadow-lg hover:shadow-app-primary/10 flex flex-col md:flex-row gap-8"
                                    >
                                        <div className="relative w-full md:w-1/2 aspect-[4/3] md:aspect-auto rounded-[2.5rem] overflow-hidden bg-gray-50 dark:bg-gray-900">
                                            <OptimizedImage
                                                src={
                                                    topSellers[0].item_image ||
                                                    topSellers[0].image_path ||
                                                    specialtiesSection.food_image_fallback
                                                }
                                                alt={topSellers[0].item_name || topSellers[0].name}
                                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                            />
                                            <div className="absolute top-5 right-5">
                                                <span className="bg-app-primary text-white text-xs font-bold py-1.5 px-4 rounded-full shadow-md flex items-center gap-1.5 uppercase tracking-wide">
                                                    <svg
                                                        className="w-3.5 h-3.5"
                                                        fill="currentColor"
                                                        viewBox="0 0 20 20"
                                                    >
                                                        <path
                                                            fillRule="evenodd"
                                                            d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z"
                                                            clipRule="evenodd"
                                                        />
                                                    </svg>
                                                    {specialtiesSection.featured_label}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="flex-1 flex flex-col justify-center">
                                            <div className="flex justify-between items-start mb-4">
                                                <div>
                                                    <h5 className="text-3xl font-black text-gray-900 dark:text-white group-hover:text-app-primary transition-colors">
                                                        {topSellers[0].item_name || topSellers[0].name}
                                                    </h5>
                                                    {topSellers[0].food_parameters !== null && (topSellers[0].portion_label || topSellers[0].portion_quantity) && (
                                                        <span className="text-sm font-bold text-gray-500 dark:text-gray-400 mt-1 block">
                                                            Portion: {topSellers[0].portion_label || `${topSellers[0].portion_quantity} ${topSellers[0].food_unit || 'pcs'}`}
                                                        </span>
                                                    )}
                                                </div>
                                                {topSellers[0].food_parameters !== null && (
                                                    <span className="text-3xl font-black text-app-primary">
                                                        {formatPrice(Number(topSellers[0].price))}
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-gray-500 dark:text-gray-400 mb-8 text-lg leading-relaxed line-clamp-3">
                                                {topSellers[0].description ||
                                                    specialtiesSection.default_food_description}
                                            </p>
                                            <AddItemWithDatePopup
                                                item={{ id: Number(topSellers[0].id), type: 'food' }}
                                                price={Number(topSellers[0].price)}
                                                isAvailable={true}
                                            />
                                        </div>
                                    </motion.div>

                                    {/* Other Top Sellers */}
                                    {topSellers.slice(1, 5).map((food: TopSeller, index: number) => (
                                        <motion.div
                                            variants={itemVariants}
                                            key={food.id}
                                            className="group bg-white dark:bg-gray-800 rounded-[2.5rem] p-5 border border-gray-100 dark:border-gray-700 hover:border-app-primary/30 transition-all duration-500 shadow-sm hover:shadow-lg hover:shadow-app-primary/10 flex flex-col gap-5"
                                        >
                                            <div className="relative aspect-[16/11] rounded-[2rem] overflow-hidden bg-gray-50 dark:bg-gray-900">
                                                <OptimizedImage
                                                    src={
                                                        food.item_image ||
                                                        food.image_path ||
                                                        specialtiesSection.food_image_fallback
                                                    }
                                                    alt={food.item_name || food.name}
                                                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                                />
                                            </div>
                                            <div className="px-1 pb-1">
                                                <div className="flex justify-between items-start mb-1">
                                                    <h5 className="text-xl font-bold text-gray-900 dark:text-white line-clamp-1 group-hover:text-app-primary transition-colors">
                                                        {food.item_name || food.name}
                                                    </h5>
                                                    {food.food_parameters !== null && (
                                                        <span className="text-xl font-black text-app-primary">
                                                            {formatPrice(Number(food.price))}
                                                        </span>
                                                    )}
                                                </div>

                                                {food.food_parameters !== null && (food.portion_label || food.portion_quantity) && (
                                                    <span className="text-xs font-bold text-gray-500 dark:text-gray-400 mb-2 block">
                                                        Portion: {food.portion_label || `${food.portion_quantity} ${food.food_unit || 'pcs'}`}
                                                    </span>
                                                )}

                                                <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 mb-6 h-10 leading-relaxed">
                                                    {food.description || specialtiesSection.default_food_description}
                                                </p>

                                                <AddItemWithDatePopup
                                                    item={{ id: Number(food.id), type: 'food' }}
                                                    price={Number(food.price)}
                                                    isAvailable={true}
                                                />
                                            </div>
                                        </motion.div>
                                    ))}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    {/* Category Browser */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] as const }}
                        className="w-full"
                    >
                        <div className="bg-gray-50 dark:bg-gray-800/40 rounded-[3.5rem] p-10 md:p-14 border border-gray-100 dark:border-gray-700 relative overflow-hidden">
                            <div className="relative z-10">
                                <div className="flex flex-col md:flex-row md:items-center justify-between mb-12 gap-6">
                                    <h4 className="text-3xl font-black text-gray-900 dark:text-white flex items-center gap-4">
                                        <span className="w-2.5 h-10 bg-app-primary rounded-full shadow-lg shadow-app-primary/30" />
                                        {specialtiesSection.categories_heading}
                                    </h4>
                                    <p className="text-gray-500 dark:text-gray-400 font-medium md:text-right max-w-xs">
                                        {specialtiesSection.footer_message}
                                    </p>
                                </div>

                                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6 md:gap-8">
                                    {categoryItems.map((cat, idx) => (
                                        <motion.div
                                            key={cat.id}
                                            initial={{ opacity: 0, scale: 0.9 }}
                                            whileInView={{ opacity: 1, scale: 1 }}
                                            viewport={{ once: true }}
                                            transition={{ delay: idx * 0.05, duration: 0.4 }}
                                        >
                                            <Link
                                                to={`/menu?categories=${cat.id}`}
                                                className="flex flex-col items-center justify-center text-center gap-4 bg-white dark:bg-gray-800 rounded-[2.5rem] p-6 md:p-10 border border-gray-50 dark:border-gray-700 hover:border-app-primary/40 shadow-sm hover:shadow-lg hover:shadow-app-primary/10 transition-all duration-500 group h-full relative overflow-hidden"
                                            >
                                                <div className="absolute inset-0 bg-gradient-to-br from-app-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                                <div className="w-20 h-20 bg-gray-50 dark:bg-gray-900 rounded-2xl flex items-center justify-center text-4xl group-hover:bg-app-primary group-hover:text-white transition-all duration-500 transform group-hover:rotate-6 group-hover:scale-110 shadow-sm relative z-10">
                                                    {cat.icon}
                                                </div>
                                                <div className="min-w-0 relative z-10">
                                                    <h3 className="font-bold text-gray-900 dark:text-white text-base group-hover:text-app-primary transition-colors mb-1">
                                                        {cat.name}
                                                    </h3>
                                                    <span className="text-[11px] text-gray-400 dark:text-gray-500 font-black uppercase tracking-widest">
                                                        {cat.count} {specialtiesSection.categories_count_suffix}
                                                    </span>
                                                </div>
                                            </Link>
                                        </motion.div>
                                    ))}
                                </div>
                            </div>

                            {/* Subtle background decoration for category section */}
                            <div className="absolute top-0 right-0 w-64 h-64 bg-app-primary/5 rounded-full blur-[80px] -z-0 translate-x-1/2 -translate-y-1/2" />
                            <div className="absolute bottom-0 left-0 w-64 h-64 bg-app-primary/5 rounded-full blur-[80px] -z-0 -translate-x-1/2 translate-y-1/2" />
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default TopSellerCategoryGrid;
